'use client';
import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import JsonLd from '@src/components/Shared/JsonLd';
import ScrollToContainer from '@src/components/Shared/ScrollToContainer';
import Container from '@src/components/Shared/Container';
import SectionHeader from '@src/components/Shared/SectionHeader';
import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import ClientSuccess from '@src/components/Sections/ClientSuccess';
import NextImage from '@src/components/Shared/NextImage';
import RenderButtons from '@src/components/Shared/RenderButtons';

import HeroPrimary from '@src/components/Sections/HeroPrimary';
import HotSpots from '@src/components/Sections/HotSpots';
import ImageTextSecondary from '@src/components/Sections/ImageTextSecondary';
import ImageTextPrimary from '@src/components/Sections/ImageTextPrimary';
import Testimonials from '@src/components/Sections/Testimonials';
import MultiQuotes from '@src/components/Sections/MultiQuotes';
import CallToAction from '@src/components/Sections/CallToAction';
import { SolutionsClientPropTypes } from './Solutions.types';
import usePreviewContent from '@src/hooks/usePreviewContent';
import constructURL from '@src/utils/constructURL';

const SolutionsClient: React.FC<SolutionsClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const page = usePreviewContent({ content: data?.page });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(
    constructURL({ languageCode: page?.languageCode, slug: '/solutions', baseURL: '' })
  );
  setContentType('site-page');

  const solutionsJsonLd = JsonLd({
    name: page?.pageName,
    pageUrl: `/${page?.languageCode?.toLowerCase()}${page?.slug === '/' ? '' : page?.slug}`,
    thumbnailUrl: page?.seoMetadata?.previewImage?.url,
    description: page?.seoMetadata?.description,
    datePublished: page?.sys?.firstPublishedAt,
    dateModified: page?.sys?.publishedAt,
    locale: page?.languageCode?.toLowerCase(),
  });

  return (
    <ScrollToContainer>
      {/* Add JSON-LD to your page */}
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(solutionsJsonLd) }}
      />
      <div className='bg-primary'>
        <HeroPrimary
          id={page?.hero?.items?.[0]?.scrollTo?.slice(1)}
          attr='hero-component'
          className='h-[50rem] lg:h-[72rem]'
          titleClassName='text-[4rem] md:text-[6rem] max-w-[36rem] md:max-w-[unset] lg:text-[8rem] 2xl:text-[11.2412rem] 2xl:tracking-[-0.2432rem] leading-[4rem] md:leading-[6rem] lg:leading-[8rem] 2xl:leading-[11.2412rem] lg:max-w-[64rem] 2xl:max-w-[96rem] 2xl:tracking-[-0.152rem] mb-[3.2rem] '
          imgClassName='max-w-[31rem] md:max-w-[36rem] bottom-[-2rem] lg:max-w-[47rem] 2xl:max-w-[62rem] mx-auto md:mx-[unset] md:z-[2] md:absolute bottom-[-3%] right-[5%] xl:right-[5%] min-h-[32.8656rem] md:min-h-[38.1672rem] lg:min-h-[49.8281rem] 2xl:min-h-[65.7312rem]'
          bodyCopyClassName='text-[1.6rem] [&>p]:text-shadow-custom md:text-[1.8rem] lg:text-[2rem] 2xl:text-[2.4rem] leading-[2.4rem] md:leading-[2.7rem] lg:leading-[3rem] 2xl:leading-[3.6rem] md:max-w-[32rem] lg:max-w-[50rem] xl:!max-w-[60rem] 2xl:max-w-[64rem]'
          title={page?.hero?.items?.[0]?.title}
          imgSrc={page?.hero?.items?.[0]?.mediaPrimary?.url}
          bodyCopy={page?.hero?.items?.[0]?.bodyCopy}
        />
      </div>

      <Container
        attr='this-is-valdhub'
        id={page?.valdHub?.items?.[0]?.scrollTo?.slice(1)}
        className='mb-[0]! mt-[20rem] md:mb-[0]! md:mt-[15rem] lg:mb-[0]!'
      >
        <SectionHeader
          className='mb-[5rem]'
          title={page?.valdHub?.items?.[0]?.title}
          pretitle={page?.valdHub?.items?.[0]?.preTitle}
          titleClassName='mb-[2rem] text-[2.8rem] font-light leading-[3.2rem] md:leading-[4.6rem] md:mb-[3.2rem] xl:text-[4.8rem] xl:leading-[5rem] 2xl:max-w-[90rem]'
          align='center'
        >
          <RenderBodyCopy bodyCopy={page?.valdHub?.items?.[0]?.bodyCopy?.json?.content} />
        </SectionHeader>

        <HotSpots
          className='mb-[8rem] p-[0]! md:mb-[14rem] 3xl:[&_figure]:left-[5%] 3xl:[&_figure]:w-[90%]'
          data={page?.valdHub?.items?.[0]}
        />
      </Container>

      <ClientSuccess
        id={page?.clientSuccess?.items?.[0]?.scrollTo?.slice(1)}
        data={page?.clientSuccess?.items?.[0]}
      />

      <ImageTextSecondary
        className='z-[-1]! lg:mb-[-1rem]! lg:mt-[-4rem]'
        id={page?.dataBeyond?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.dataBeyond?.items?.[0]?.title}
        imgURL={page?.dataBeyond?.items?.[0]?.mediaPrimary?.url}
        imgAlt={page?.dataBeyond?.items?.[0]?.mediaPrimary?.description ?? ''}
        childrenElements={
          <>
            <RenderBodyCopy bodyCopy={page?.dataBeyond?.items?.[0]?.bodyCopy?.json?.content} />
          </>
        }
      />

      <NextImage
        id={page?.weExist?.items?.[0]?.scrollTo?.slice(1)}
        src={page?.weExist?.items?.[0]?.mediaPrimary?.url}
        alt='A portrait'
        className='mb-[4.5rem] h-[40rem] md:h-[45rem] lg:h-[54.3rem] 2xl:mb-[5.6rem] [&>img]:object-top'
      >
        <Container className='mb-[0]! h-full'>
          <SectionHeader
            className='h-full'
            preTitleClassName='mb-[1.6rem]'
            title={page?.weExist?.items?.[0]?.title}
            titleClassName='text-center text-[2.2rem] leading-[2.6rem] tracking-[-0.0418] text-body-copy--light font-light text-[1.8rem] md:leading-[3rem] md:text-[2.8rem] lg:max-w-[110rem] lg:text-[4.8rem] 2xl:text-[7.2rem] lg:leading-[5rem] 2xl:leading-[7.56rem]'
            childrenClassName='relative max-w-[88.8rem]!'
            titleHighlightBold
            align='center'
          >
            <RenderBodyCopy
              className='mx-auto mt-[2.4rem] max-w-full! text-left text-[1.6rem] font-extralight text-body-copy--light md:text-center md:text-[2.4rem]  2xl:mt-[3.2rem] 2xl:max-w-[88.8rem]'
              bodyCopy={page?.weExist?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons
              className='justify-center'
              items={page?.weExist?.items?.[0]?.buttonCollection?.items}
            />
          </SectionHeader>
        </Container>
      </NextImage>

      <ImageTextSecondary
        reversed
        id={page?.betterManagement?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.betterManagement?.items?.[0]?.preTitle}
        title={page?.betterManagement?.items?.[0]?.title}
        imgURL={page?.betterManagement?.items?.[0]?.extraAssetsCollection?.items?.[0]?.media?.url}
        imgAlt={
          page?.betterManagement?.items?.[0]?.extraAssetsCollection?.items?.[0]?.media
            ?.description ?? ''
        }
        imgSecondClassName='[&>figcaption]:mt-[0rem]! [&>figcaption]:leading-[3rem]! [&>figcaption>div>p>a]:drop-shadow-xs'
        imgFirstClassName='[&>figcaption]:mt-[0rem]! [&>figcaption]:leading-[3rem]! [&>figcaption>div>p>a]:drop-shadow-xs'
        imgCaption={
          <>
            <RenderBodyCopy
              bodyCopy={
                page?.betterManagement?.items?.[0]?.extraAssetsCollection?.items?.[0]?.bodyCopy
                  ?.json?.content
              }
            />
          </>
        }
        childrenElements={
          <>
            <RenderBodyCopy
              bodyCopy={page?.betterManagement?.items?.[0]?.bodyCopy?.json?.content}
            />
          </>
        }
      />

      <ImageTextPrimary
        id={page?.measurement?.items?.[0]?.scrollTo?.slice(1)}
        attr='itp-digital-health-component'
        imgURL={page?.measurement?.items?.[0]?.extraAssetsCollection?.items?.[0]?.media?.url}
        imgLink={page?.measurement?.items?.[0]?.extraAssetsCollection?.items?.[0]?.url}
        imgAlt={
          page?.measurement?.items?.[0]?.extraAssetsCollection?.items?.[0]?.media?.description
        }
        pretitle={page?.measurement?.items?.[0]?.preTitle}
        title={page?.measurement?.items?.[0]?.title}
        childrenElements={
          <>
            <RenderBodyCopy
              bodyCopy={page?.measurement?.items?.[0]?.bodyCopy?.json?.content}
              className='[&>p>a]:drop-shadow-xs'
            />
          </>
        }
      />

      <ImageTextSecondary
        reversed
        id={page?.normativeData?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.normativeData?.items?.[0]?.preTitle}
        title={page?.normativeData?.items?.[0]?.title}
        imgURL={page?.normativeData?.items?.[0]?.mediaPrimary?.url}
        imgAlt={page?.normativeData?.items?.[0]?.mediaPrimary?.description ?? ''}
        childrenElements={
          <>
            <RenderBodyCopy bodyCopy={page?.normativeData?.items?.[0]?.bodyCopy?.json?.content} />
          </>
        }
      />

      <Container
        attr='normative-download-btn'
        className='mt-[-2rem] text-center md:mt-[-3rem] lg:mt-[-5rem] xl:mt-[-4.8rem]'
      >
        <RenderButtons
          className='justify-left lg:justify-center'
          items={page?.normativeData?.items?.[0]?.buttonCollection?.items}
        />
      </Container>

      <Testimonials
        id={page?.testimonials?.items?.[0]?.scrollTo?.slice(1)}
        testimonials={page?.testimonials?.items?.[0]?.topicCollection?.items}
      />

      <ImageTextPrimary
        attr='itp-patients-component'
        id={page?.patients?.items?.[0]?.scrollTo?.slice(1)}
        reversed
        imgURL={page?.patients?.items?.[0]?.mediaPrimary?.url}
        imgLink={page?.patients?.items?.[0]?.extraAssetsCollection?.items?.[0]?.url}
        imgAlt={page?.patients?.items?.[0]?.mediaPrimary?.description}
        pretitle={page?.patients?.items?.[0]?.preTitle}
        title={page?.patients?.items?.[0]?.title}
        childrenElements={
          <>
            <RenderBodyCopy bodyCopy={page?.patients?.items?.[0]?.bodyCopy?.json?.content} />
          </>
        }
      />

      <ImageTextPrimary
        attr='itp-clinicians-component'
        id={page?.clinicians?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.clinicians?.items?.[0]?.preTitle}
        title={page?.clinicians?.items?.[0]?.title}
        imgURL={page?.clinicians?.items?.[0]?.mediaPrimary?.url}
        childrenElements={
          <>
            <RenderBodyCopy bodyCopy={page?.clinicians?.items?.[0]?.bodyCopy?.json?.content} />
          </>
        }
      />

      <NextImage
        src={page?.weExistV2?.items?.[0]?.mediaPrimary?.url}
        id={page?.weExistV2?.items?.[0]?.scrollTo?.slice(1)}
        alt='A portrait'
        className='mb-[4.5rem] h-[40rem] md:h-[45rem] lg:h-[54.3rem] 2xl:mb-[4.5rem] [&>img]:object-top'
      >
        <Container className='mb-[0]! h-full'>
          <SectionHeader
            className='h-full'
            preTitleClassName='mb-[1.6rem]'
            title={page?.weExistV2?.items?.[0]?.title}
            titleClassName='text-center text-[2.2rem] leading-[2.6rem] tracking-[-0.0418] text-body-copy--light font-light text-[1.8rem] md:leading-[3rem] md:text-[2.8rem] lg:max-w-[70rem] lg:text-[4.8rem] 2xl:text-[7.2rem] lg:leading-[5rem] 2xl:leading-[7.56rem]'
            titleHighlightBold
            childrenClassName='relative max-w-[88.8rem]!'
            align='center'
          >
            <RenderBodyCopy
              className='mx-auto mt-[2.4rem] max-w-full! text-left text-[1.6rem] font-extralight text-body-copy--light md:text-center md:text-[2.4rem]  2xl:mt-[3.2rem] 2xl:max-w-[88.8rem]'
              bodyCopy={page?.weExistV2?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons
              className='justify-center'
              items={page?.weExistV2?.items?.[0]?.buttonCollection?.items}
            />
          </SectionHeader>
        </Container>
      </NextImage>

      <ImageTextPrimary
        attr='itp-manage-your-patients'
        id={page?.likePros?.items?.[0]?.scrollTo?.slice(1)}
        imgURL={page?.likePros?.items?.[0]?.mediaPrimary?.url}
        pretitle={page?.likePros?.items?.[0]?.preTitle}
        title={page?.likePros?.items?.[0]?.title}
        childrenElements={
          <>
            <RenderBodyCopy bodyCopy={page?.likePros?.items?.[0]?.bodyCopy?.json?.content} />
          </>
        }
      />

      <Container className='w-full'>
        <NextImage
          className='hidden sm:hidden md:block lg:block xl:block'
          entryId={page?.securityPrivacyPolicy?.items?.[0]?.sys?.id}
          src={page?.securityPrivacyPolicy?.items?.[0]?.mediaPrimary?.url}
          alt={page?.securityPrivacyPolicy?.items?.[0]?.mediaPrimary?.description}
          aspectRatio={
            page?.securityPrivacyPolicy?.items?.[0]?.mediaPrimary?.width /
            page?.securityPrivacyPolicy?.items?.[0]?.mediaPrimary?.height
          }
          width={page?.securityPrivacyPolicy?.items?.[0]?.mediaPrimary?.width}
        />
        <NextImage
          className='block sm:block md:hidden lg:hidden xl:hidden'
          entryId={page?.securityPrivacyPolicy?.items?.[0]?.sys?.id}
          src={page?.securityPrivacyPolicy?.items?.[0]?.mediaSecondary?.url}
          alt={page?.securityPrivacyPolicy?.items?.[0]?.mediaSecondary?.description}
          aspectRatio={
            page?.securityPrivacyPolicy?.items?.[0]?.mediaSecondary?.width /
            page?.securityPrivacyPolicy?.items?.[0]?.mediaSecondary?.height
          }
          width={page?.securityPrivacyPolicy?.items?.[0]?.mediaSecondary?.width}
        />
      </Container>

      <SectionHeader
        id={page?.outSolutions?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.outSolutions?.items?.[0]?.title}
        titleClassName='mb-[2rem] text-[2.8rem] font-light leading-[3.2rem] md:leading-[4.6rem] md:mb-[3.2rem] xl:text-[4.8rem] xl:leading-[5rem] 2xl:max-w-[90rem]'
      />

      <MultiQuotes
        testimonials={page?.otherTestimonials?.items?.[0]?.topicCollection?.items}
        id={page?.testimonials?.items?.[0]?.scrollTo?.slice(1)}
      />

      <CallToAction
        attr='call-to-action-component'
        id={page?.callToAction?.items?.[0]?.scrollTo?.slice(1)}
        title={data.page?.callToAction?.items?.[0]?.title}
        bodyCopy={page?.callToAction?.items?.[0]?.bodyCopy?.json?.content}
        buttons={page?.callToAction?.items?.[0]?.buttonCollection?.items}
      />
    </ScrollToContainer>
  );
};

export default SolutionsClient;
